import { Patterns } from "@omni/schemas/input/patterns";
import { addMethod, StringSchema, string as yupString } from "yup";

import { create } from "@/api/api";
import { Translate } from "@/redux/modules/value/translations/translations.provider";
import * as NeverbounceService from "@/services/neverbounce.service";
import { hasLocalStorageTimestamp } from "@/utils/bonusCard";
import { Config } from "@/utils/config.utils";

import { string } from "./general.RHF";

const checkEmailAxios = create("", true, {
    headers: {},
    validateStatus: (status) => {
        return status === 404 || (status >= 200 && status < 400);
    }
});

addMethod(yupString, "validateEmailRHF", function (checkExisting) {
    return this.test("validateEmail", function (value) {
        const invalidatedErrorMessage = "registerForm.error.invalidatedEmail";
        const existingMailErrorMessage = "registerForm.error.existingUser";

        return new Promise((resolve, reject) => {
            const checkExistingEmail = () => {
                if (!checkExisting) {
                    resolve(true);
                    return;
                }

                checkEmailAxios
                    .head(
                        `${Config.general["api-base-url"]}/customerservice/${Config.general["basestore"]}/customers/${value}`,
                        {
                            cache: {
                                methods: ["head"],
                                ttl: 30000, //ms
                                cachePredicate: {
                                    statusCheck: (status) =>
                                        status >= 200 && status <= 404
                                }
                            }
                        }
                    )
                    .then(({ status }) => {
                        if (status === 404) {
                            resolve(true);
                        } else {
                            reject(
                                this.createError({
                                    message: () => () => (t: Translate) =>
                                        t(existingMailErrorMessage)
                                })
                            );
                        }
                    })
                    .catch(() => {
                        return resolve(true);
                    });
            };

            if (value && Patterns[Config.general.basestore].email.test(value)) {
                NeverbounceService.validateEmailAddress(value).then(
                    (result) => {
                        if (result) {
                            checkExistingEmail();
                        } else {
                            reject(
                                this.createError({
                                    message: () => () => (t: Translate) =>
                                        t(invalidatedErrorMessage)
                                })
                            );
                        }
                    },
                    () => checkExistingEmail()
                );
            } else {
                reject(
                    this.createError({
                        message: () => () => (t: Translate) =>
                            t(invalidatedErrorMessage)
                    })
                );
            }
        });
    });
});

addMethod<StringSchema>(yupString, "isEqualPasswordRHF", function () {
    return this.test("isEqualPasswordRHF", function (value) {
        const { createError, parent: formValues } = this;

        const passwordValue = formValues.newPassword;

        return (
            (passwordValue && value && passwordValue === value) ||
            createError({
                message: () => () => (t: Translate) =>
                    t("general.form.input.validation.passwordRepeat.invalid")
            })
        );
    });
});

addMethod<StringSchema>(yupString, "bonusCardRequestedRHF", function () {
    return this.test("bonusCardRequestedRHF", function (value) {
        const hasNoValue = !!value && !hasLocalStorageTimestamp(value);

        return (
            hasNoValue ||
            this.createError({
                message: () => () => (t: Translate) =>
                    t(
                        "general.form.input.validation.email.bonusCardRequested.invalid"
                    )
            })
        );
    });
});

export const optionalFirstName = string().nullable().max(35);
export const firstName = string().required().max(35);

export const lastName = string().nullable().required().max(35);
export const optionalLastName = string().nullable().max(35);

export const password = string().nullable().required().min(8);

export const passwordRepeat = string()
    .nullable()
    .required()
    .min(8)
    .isEqualPasswordRHF();

export const email = string().nullable().required().trim().email();

export const emailWithBonusCardState = string()
    .nullable()
    .required()
    .trim()
    .email()
    .bonusCardRequestedRHF();

export const emailWithNB = string()
    .nullable()
    .required()
    .trim()
    .email()
    .validateEmailRHF(false);

export const checkExistingEmail = string()
    .nullable()
    .required()
    .trim()
    .email()
    .validateEmailRHF(true);
